<template>
  <div class="flex flex-col gap-2">
    <template v-if="negationEnabled">
      <InfuseNegatableFilter
        :model-value="negated"
        @update:model-value="(newValue) => valuesChanged(selectedOption, newValue)"
      >
        <InfuseSearchSelect
          :model-value="selectedOption"
          :options="options"
          :placeholder="t('placeholder')"
          name="select-filter"
          size="small"
          required
          @update:model-value="(newValue) => valuesChanged(newValue, negated)"
        >
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="text-neutral-400" />
        </InfuseSearchSelect>
      </InfuseNegatableFilter>
    </template>
    <template v-else>
      <InfuseSearchSelect
        :model-value="selectedOption"
        :options="options"
        :placeholder="t('placeholder')"
        name="select-filter"
        size="small"
        required
        @update:model-value="(newValue) => valuesChanged(newValue, false)"
      >
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="text-neutral-400" />
      </InfuseSearchSelect>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { FilterOption } from '@infuse/ui/domain';
import { computed, onMounted, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { InfuseNegatableFilter, InfuseSearchSelect } from '../../index';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { t } = useI18n();

interface Props {
  modelValue: FilterOption[];
  negationEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  negationEnabled: true
});

interface Emits {
  (e: 'apply:value', value: FilterOption, negated: boolean): void;

  (e: 'validationChange', valid: boolean): void;
}

const emit = defineEmits<Emits>();

const options = computed(() => {
  return props.modelValue.map((fo) => {
    return { key: fo.label(), value: fo.name, name: fo.name };
  });
});

const selectedOption: Ref<string> = ref('');
const negated = ref(false);

onMounted(() => {
  valuesChanged(selectedOption.value, props.negationEnabled ? negated.value : false);
});

function valuesChanged(newValue: string, newNegated: boolean): void {
  selectedOption.value = newValue;
  negated.value = props.negationEnabled ? newNegated : false;
  const option = props.modelValue.find((o) => o.name === selectedOption.value);

  if (option) {
    emit('apply:value', option, negated.value);
    emit('validationChange', !!selectedOption.value);
  }
}
</script>

